import React from "react"
import PropTypes from "prop-types"
import { cartCount, getRestaurantId, setRestaurantId, getState, saveState, clearCart } from '../actions/storage'
import ItemMenue from './ItemMenue'
import CartDetails from './CartDetails'

const BG = '#000000'
const TEXT = '#ffffff'
const TEXT_MUTED = 'rgba(255,255,255,0.62)'
const TEXT_FAINT = 'rgba(255,255,255,0.45)'
const HAIRLINE = 'rgba(255,255,255,0.14)'

const DISPLAY = "'Bebas Neue', 'Oswald', 'Anton', 'Impact', sans-serif"
const SANS = "'Inter', 'Helvetica Neue', 'Helvetica', Arial, sans-serif"

const styles = {
  page: {
    minHeight: '100vh',
    background: BG,
    color: TEXT,
    fontFamily: SANS,
    paddingBottom: 80,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },

  banner: {
    position: 'relative',
    padding: '40px 28px 28px',
    textAlign: 'left',
    overflow: 'hidden',
  },

  bannerActions: {
    position: 'absolute',
    top: 16,
    right: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    zIndex: 2,
  },

  bannerHero: {
    width: '100%',
    aspectRatio: '4 / 3',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    marginBottom: 28,
    boxShadow: '0 24px 40px rgba(0,0,0,0.45)',
  },

  bannerTitle: {
    fontFamily: DISPLAY,
    color: TEXT,
    fontSize: 'clamp(56px, 14vw, 96px)',
    fontWeight: 400,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    margin: 0,
    lineHeight: 0.92,
  },

  bannerKicker: {
    fontFamily: SANS,
    color: TEXT_FAINT,
    fontSize: 11,
    letterSpacing: '0.32em',
    textTransform: 'uppercase',
    margin: '0 0 18px',
    fontWeight: 500,
  },

  bannerSubtitle: {
    color: TEXT_MUTED,
    fontSize: 14,
    margin: '18px 0 0',
    maxWidth: 460,
    lineHeight: 1.55,
    fontWeight: 300,
  },

  categoriesWrap: {
    marginTop: 8,
  },

  category: {
    background: 'transparent',
    borderTop: `1px solid ${HAIRLINE}`,
  },

  categoryHeader: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    padding: '40px 28px 18px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: TEXT,
    gap: 16,
  },

  categoryTitle: {
    fontFamily: DISPLAY,
    fontSize: 'clamp(48px, 12vw, 76px)',
    fontWeight: 400,
    letterSpacing: '0.01em',
    textTransform: 'uppercase',
    margin: 0,
    textAlign: 'left',
    color: TEXT,
    lineHeight: 0.95,
  },

  chevron: {
    fontSize: 14,
    color: TEXT_MUTED,
    transition: 'transform 0.25s ease',
    paddingBottom: 8,
  },

  chevronOpen: {
    transform: 'rotate(180deg)',
  },

  itemList: {
    padding: '0 28px 28px',
  },

  itemRow: {
    display: 'flex',
    gap: 16,
    padding: '22px 0',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    borderTop: `1px solid ${HAIRLINE}`,
    width: '100%',
    textAlign: 'left',
    color: TEXT,
    alignItems: 'flex-start',
  },

  itemContent: {
    flex: 1,
    minWidth: 0,
  },

  itemTitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 16,
  },

  itemName: {
    fontFamily: SANS,
    fontSize: 14,
    fontWeight: 600,
    color: TEXT,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    margin: 0,
    lineHeight: 1.25,
  },

  itemPrice: {
    fontFamily: SANS,
    fontSize: 15,
    fontWeight: 500,
    color: TEXT,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },

  itemDescription: {
    fontFamily: SANS,
    fontSize: 13,
    color: TEXT_MUTED,
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: 1.55,
    marginTop: 8,
  },

  itemImage: {
    width: 92,
    height: 92,
    borderRadius: '50%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255,255,255,0.04)',
    flexShrink: 0,
  },

  taccBadge: {
    height: 16,
    marginLeft: 8,
    verticalAlign: 'middle',
    opacity: 0.85,
  },

  loadingWrap: {
    minHeight: '100vh',
    background: BG,
    color: TEXT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    fontFamily: SANS,
    fontWeight: 400,
  },
}

class RestaurantMenu extends React.Component {
  state = {
    restaurantDetails: null,
    loading: true,
    cartItemsCount: 0,
    expandedCategories: {},
    delivery: 'delivery',
    customer: { name: '', address: '', payment: 'cash' },
  }

  componentDidMount() {
    this.loadData()
  }

  async loadData() {
    setRestaurantId(this.props.restaurantId)
    clearCart()

    await this.setState({ loading: true })
    const newState = { cartItemsCount: 0 }
    try {
      const restaurantDetails = this.props.restaurantData
      newState.restaurantDetails = restaurantDetails
      if (restaurantDetails && restaurantDetails.categories) {
        const expanded = {}
        if (restaurantDetails.categories[0]) {
          expanded[restaurantDetails.categories[0].id] = true
        }
        newState.expandedCategories = expanded
      }
    } finally {
      newState.loading = false
      this.setState(newState)
      saveState(newState)
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat("es-AR", { style: 'currency', currency: 'ARS' }).format(price)
  }

  itemPrice(menu) {
    if (menu.use_portion_price && menu.available_portions && menu.available_portions.length > 0) {
      return this.formatPrice(menu.available_portions[0].price)
    }
    if (menu.price > 0) return this.formatPrice(menu.price)
    return ''
  }

  toggleCategory(categoryId) {
    this.setState(prev => ({
      expandedCategories: {
        ...prev.expandedCategories,
        [categoryId]: !prev.expandedCategories[categoryId],
      },
    }))
  }

  setDelivery(mode) {
    this.setState({ delivery: mode })
  }

  setCustomerField(field, value) {
    this.setState(prev => ({ customer: { ...prev.customer, [field]: value } }))
  }

  onItemAdded() {
    this.setState({ cartItemsCount: cartCount() })
  }

  onItemChanged() {
    this.setState({ cartItemsCount: cartCount() })
  }

  renderBanner(restaurantDetails) {
    return (
      <header style={styles.banner}>
        <div style={styles.bannerActions}>
          <CartDetails
            cartItemsCount={this.state.cartItemsCount}
            onItemChanged={() => this.onItemChanged()}
          />
        </div>

        {restaurantDetails.image_original && (
          <div
            style={{
              ...styles.bannerHero,
              backgroundImage: `url(${restaurantDetails.image_original})`,
            }}
          />
        )}

        <p style={styles.bannerKicker}>— Carta —</p>
        <h1 style={styles.bannerTitle}>{restaurantDetails.name}</h1>
        {restaurantDetails.description && (
          <p style={styles.bannerSubtitle}>{restaurantDetails.description}</p>
        )}
      </header>
    )
  }


  renderItemRow(menu, restaurantImage) {
    const image = menu.image_original || restaurantImage
    return (
      <button
        key={`menu-${menu.id}`}
        type="button"
        style={styles.itemRow}
        data-toggle="modal"
        data-target={`#item-menu-${menu.id}`}
      >
        {image && (
          <div style={{ ...styles.itemImage, backgroundImage: `url(${image})` }} />
        )}
        <div style={styles.itemContent}>
          <div style={styles.itemTitleRow}>
            <span style={styles.itemName}>
              {menu.name}
              {menu.tacc_free && (
                <img src={this.props.imgSinTaccSrc} style={styles.taccBadge} alt="sin TACC" />
              )}
            </span>
            <span style={styles.itemPrice}>{this.itemPrice(menu)}</span>
          </div>
          {menu.description && (
            <div style={styles.itemDescription}>{menu.description}</div>
          )}
        </div>
      </button>
    )
  }

  renderCategory(category, restaurantImage) {
    const isOpen = !!this.state.expandedCategories[category.id]
    const menues = (category.menues || []).slice().sort((a, b) => (a.order_number || 0) - (b.order_number || 0))

    return (
      <section key={`category-${category.id}`} id={`category-${category.id}`} style={styles.category}>
        <button
          type="button"
          style={styles.categoryHeader}
          onClick={() => this.toggleCategory(category.id)}
          aria-expanded={isOpen}
        >
          <span>
            <span style={styles.categoryTitle}>{category.name}</span>
          </span>
          <span style={{ ...styles.chevron, ...(isOpen ? styles.chevronOpen : {}) }}>
            <i className="fas fa-chevron-down"></i>
          </span>
        </button>

        {isOpen && (
          <div style={styles.itemList}>
            {menues.map(menu => this.renderItemRow(menu, restaurantImage))}
          </div>
        )}

        {menues.map(menu => (
          <ItemMenue
            key={`modal-${menu.id}`}
            menueId={menu.id}
            imgSinTaccSrc={this.props.imgSinTaccSrc}
            categoryName={category.name}
            restaurantImage={restaurantImage}
            backgroundSrc={this.props.backgroundSrc}
            onItemAdded={() => this.onItemAdded()}
          />
        ))}
      </section>
    )
  }

  render() {
    if (this.state.loading) {
      return <div style={styles.loadingWrap}>Cargando...</div>
    }

    const { restaurantDetails } = this.state
    if (!restaurantDetails) {
      return <div style={styles.loadingWrap}>Error al cargar el restaurante...</div>
    }

    return (
      <div style={styles.page}>
        {this.renderBanner(restaurantDetails)}

        <div style={styles.categoriesWrap}>
          {restaurantDetails.categories.map(category =>
            this.renderCategory(category, restaurantDetails.image_original)
          )}
        </div>

      </div>
    )
  }
}

RestaurantMenu.propTypes = {
  restaurantId: PropTypes.string,
  store: PropTypes.string,
  imgSrc: PropTypes.string,
  imgIosSrc: PropTypes.string,
  imgAndroidSrc: PropTypes.string,
  dotIconSrc: PropTypes.string,
  ddsIconSrc: PropTypes.string,
  imgSinTaccSrc: PropTypes.string,
  backgroundSrc: PropTypes.string,
  restaurantData: PropTypes.object,
}

export default RestaurantMenu
